import type {ReactNode} from 'react'
import type {MetaDescriptor} from 'react-router'

import {
    buildFAQPage,
    buildImageGallery,
    buildWebApplication,
    jsonLd,
    type JsonLd,
    type WebApplicationOptions,
} from '@core/lib/jsonld'
import type {PackIndexStyles} from '@core/packs/PackIndex'
import type {PackIndexItem, SeriesGroup} from '@core/packs/series/types'
import type {AnonTile, TileSummary} from '@core/wall/contract'

/**
 * A wall home's doorstep: what the house serves a signed-out visitor and a crawler under its wall —
 * head meta, the JSON-LD blocks, the endpaper. Each house declares it once; `HomeDoorstep` renders
 * it, and every structured-data block is derived from what the same document renders.
 */
export type HouseIdentity = {
    /** 'https://narcis.ai' — no trailing slash; every absolute URL is built from it. */
    origin: string
    meta: {title: string; description: string}
    organization: OrganizationFields
    webApplication: WebApplicationOptions
    /** The house theme's own builder (root-relative); the gallery makes it absolute. */
    tileImageUrl: (imageKey: string) => string
}

export type OrganizationFields = {
    name: string
    logo?: string
    description?: string
    parentOrganization?: object
}

export type HomeLink = {label: string; href: string}
export type HomeFaqEntry = {question: string; answer: string}

export type EndpaperContent = {
    intro: {h1: string; lede: ReactNode; stats?: ReactNode}
    catalog?: {h2: string; headerLinks?: HomeLink[]; links: HomeLink[]; seeAll?: HomeLink}
    sections?: {h2: string; body: ReactNode}[]
    /** ONE source: the accordion renders it and FAQPage is built from it. */
    faq?: {h2?: string; entries: HomeFaqEntry[]; iconSize?: number}
    packIndex?: {groups: SeriesGroup<PackIndexItem>[]; total: number; styles: PackIndexStyles}
    /** Rendered last, inside the endpaper (badge strips, a footer). */
    coda?: ReactNode
}

/** The house's own SCSS module — the same injected-styles pattern `PackIndex` uses. */
export type EndpaperStyles = Record<string, string>

export type HouseHome = HouseIdentity & {endpaper: EndpaperContent; styles: EndpaperStyles}

/** A tile the document server-renders — the gallery keeps the ones carrying a picture. */
export type GalleryTile = TileSummary | AnonTile

export function homeMeta(house: Pick<HouseIdentity, 'origin' | 'meta'>): MetaDescriptor[] {
    return [
        {title: house.meta.title},
        {name: 'description', content: house.meta.description},
        {property: 'og:title', content: house.meta.title},
        {property: 'og:description', content: house.meta.description},
        {tagName: 'link', rel: 'canonical', href: `${house.origin}/`},
    ]
}

export function buildHomeOrganization(origin: string, fields: OrganizationFields): JsonLd {
    const {name, ...rest} = fields
    return jsonLd('Organization', {name, url: origin, ...rest})
}

/** The document's JSON-LD blocks, in render order: Organization, WebApplication, FAQPage when the
 *  endpaper declares a FAQ, ImageGallery when `gallery` holds a picture. */
export function homeStructuredData(house: HouseHome, gallery: readonly GalleryTile[]): JsonLd[] {
    const blocks = [
        buildHomeOrganization(house.origin, house.organization),
        buildWebApplication(house.webApplication),
    ]
    const faq = house.endpaper.faq
    if (faq) blocks.push(buildFAQPage(faq.entries.map((e) => ({q: e.question, a: e.answer}))))

    const images = gallery.flatMap((tile) =>
        'image_key' in tile
            ? [{
                contentUrl: new URL(house.tileImageUrl(tile.image_key), `${house.origin}/`).href,
                width: tile.width,
                height: tile.height,
            }]
            : [],
    )
    if (images.length > 0) {
        blocks.push(buildImageGallery({name: house.meta.title, url: `${house.origin}/`, images}))
    }
    return blocks
}
